/**
 * /onboarding — multi-step wizard (Story Onboarding-2).
 *
 * Wizard shell + steps 1 (Identity) and 2 (Profissional). Steps 3–8 render
 * placeholders so the progress bar advances and dogfooding works.
 *
 * Redirects to `/` when the user is already 100% complete (don't trap
 * finished users on the form).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { OnboardingLayout } from "@/features/onboarding/components/OnboardingLayout";
import { OnboardingComplete } from "@/features/onboarding/components/OnboardingComplete";
import { useOnboardingStore } from "@/features/onboarding/store/onboardingStore";
import { STEPS, TOTAL_STEPS, findStepIndexBySlug } from "@/features/onboarding/config/steps";
import { ApiKeysStep } from "@/features/onboarding/steps/ApiKeysStep";
import { IdentityStep } from "@/features/onboarding/steps/IdentityStep";
import { ProfessionalStep } from "@/features/onboarding/steps/ProfessionalStep";
import { PositioningStep } from "@/features/onboarding/steps/PositioningStep";
import { PersonalityStep } from "@/features/onboarding/steps/PersonalityStep";
import { VoiceStep } from "@/features/onboarding/steps/VoiceStep";
import { AudienceStep } from "@/features/onboarding/steps/AudienceStep";
import { ProceduresStep } from "@/features/onboarding/steps/ProceduresStep";
import { FunnelStep } from "@/features/onboarding/steps/FunnelStep";
import { PlaceholderStep } from "@/features/onboarding/steps/PlaceholderStep";
import { useBusinessContext } from "@/hooks/useBusinessContext";
import { useUpdateBusinessContext } from "@/features/onboarding/hooks/useUpdateBusinessContext";

export default function Onboarding() {
  const [params, setParams] = useSearchParams();
  const { query } = useBusinessContext();
  const { isPending: isSaving, isError: saveError } = useUpdateBusinessContext();

  const currentStepIndex = useOnboardingStore((s) => s.currentStepIndex);
  const goNext = useOnboardingStore((s) => s.goNext);
  const goTo = useOnboardingStore((s) => s.goTo);
  const setSubmitting = useOnboardingStore((s) => s.setSubmitting);
  const isSubmittingStep = useOnboardingStore((s) => s.isSubmittingStep);

  // URL ↔ store sync. ONE effect with prev-value refs picks an authority
  // per dispatch: if URL just changed → store follows; if store just changed
  // → URL follows. Two separate effects raced on stale closures and
  // ping-ponged when URL and store disagreed at mount (regression repro:
  // open /onboarding?step=posicionamento with currentStepIndex=0 in store).
  const prevUrlSlug = useRef<string | null>(null);
  const prevStoreIdx = useRef<number>(currentStepIndex);

  useEffect(() => {
    const urlSlug = params.get("step");
    const urlIdx = urlSlug ? findStepIndexBySlug(urlSlug) : null;

    const urlChanged = urlSlug !== prevUrlSlug.current;
    const storeChanged = currentStepIndex !== prevStoreIdx.current;
    const isInitialMount = prevUrlSlug.current === null && !storeChanged;

    if ((urlChanged || isInitialMount) && urlIdx !== null && urlIdx !== currentStepIndex) {
      // URL is authoritative — sync store to URL.
      goTo(urlIdx);
    } else if (storeChanged && (urlIdx === null || urlIdx !== currentStepIndex)) {
      // Store advanced via user action — push URL to match.
      const expected = STEPS[currentStepIndex].slug;
      if (params.get("step") !== expected) {
        const next = new URLSearchParams(params);
        next.set("step", expected);
        setParams(next, { replace: true });
      }
    }

    prevUrlSlug.current = urlSlug;
    prevStoreIdx.current = currentStepIndex;
  }, [currentStepIndex, params, setParams, goTo]);

  const [stepValid, setStepValid] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const continueFnRef = useRef<() => Promise<boolean>>(async () => true);

  // Completion screen is shown only when the user explicitly finishes the last
  // step via handleContinue. We do NOT auto-show based on isComplete because
  // the query resolves asynchronously: on mount isComplete is false (loading),
  // then flips true — which would incorrectly trigger the celebration for
  // returning users who just want to edit their profile.
  const [showCompletion, setShowCompletion] = useState(false);

  const onValidityChange = useCallback((v: boolean) => setStepValid(v), []);
  const onMissingChange = useCallback((labels: string[]) => setMissingFields(labels), []);

  // Reset hint when stepping; the new step will recompute its own missing list.
  useEffect(() => {
    setMissingFields([]);
  }, [currentStepIndex]);
  const onAttemptContinue = useCallback((fn: () => Promise<boolean>) => {
    continueFnRef.current = fn;
  }, []);

  const handleContinue = useCallback(async () => {
    setSubmitting(true);
    try {
      const ok = await continueFnRef.current();
      if (!ok) return;
      // Last step: flow is finished regardless of completeness_pct (the pct
      // measures content quality, not flow completion). Trigger the
      // celebration screen here; the redirect-on-isComplete effect handles
      // returning users separately.
      if (currentStepIndex >= TOTAL_STEPS - 1) {
        setShowCompletion(true);
      } else {
        goNext();
      }
    } finally {
      setSubmitting(false);
    }
  }, [currentStepIndex, goNext, setSubmitting]);

  const saveStatus = useMemo<"idle" | "saving" | "saved" | "error">(() => {
    if (saveError) return "error";
    if (isSaving) return "saving";
    if (query.data) return "saved";
    return "idle";
  }, [isSaving, saveError, query.data]);

  if (showCompletion) return <OnboardingComplete redirectTo="/" />;
  // Don't auto-redirect users with a complete profile away from /onboarding —
  // they may be returning to edit. The completion celebration is already
  // gated behind `showCompletion` (only set when finishing in this session).

  const step = STEPS[currentStepIndex];

  return (
    <OnboardingLayout
      canContinue={stepValid}
      onContinue={handleContinue}
      isContinuing={isSubmittingStep}
      saveStatus={saveStatus}
      missingFields={missingFields}
    >
      <AnimatePresence mode="wait" initial={false}>
        <div key={step.slug} className="flex flex-1 flex-col">
          {(() => {
            const stepProps = { onValidityChange, onAttemptContinue, onMissingChange };
            if (step.placeholder) {
              return (
                <PlaceholderStep
                  step={step}
                  onValidityChange={onValidityChange}
                  onAttemptContinue={onAttemptContinue}
                />
              );
            }
            switch (step.slug) {
              case "conexoes":
                return <ApiKeysStep {...stepProps} />;
              case "identidade":
                return <IdentityStep {...stepProps} />;
              case "profissional":
                return <ProfessionalStep {...stepProps} />;
              case "posicionamento":
                return <PositioningStep {...stepProps} />;
              case "codigo-interno":
                return <PersonalityStep {...stepProps} />;
              case "voz":
                return <VoiceStep {...stepProps} />;
              case "procedimentos":
                return <ProceduresStep {...stepProps} />;
              case "funil":
                return <FunnelStep {...stepProps} />;
              case "publico":
                return <AudienceStep {...stepProps} />;
              default:
                return (
                  <PlaceholderStep
                    step={step}
                    onValidityChange={onValidityChange}
                    onAttemptContinue={onAttemptContinue}
                  />
                );
            }
          })()}
        </div>
      </AnimatePresence>
    </OnboardingLayout>
  );
}
